import type { TipoAlvoImpacto } from '../../didatico/alvosImpacto'
import type { PrioridadeTriagem } from '../../didatico/ocupantes'
import type { ModoRetencao } from '../../didatico/retencaoOcupante'
import type { TipoColisao } from '../../didatico/timelineCinematica'
import type { CenaTabletop, MaterialAbsorvente } from '../tipos'

export type EtapaAvaliacao =
  | 'impressao_geral'
  | 'X'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'sample'
  | 'secundaria'
  | 'sinais'

export type IdAcaoAvaliacao =
  | 'chegada_queixa_aparencia'
  | 'chegada_instrucao_ator'
  | 'x_hemorragia_externa'
  | 'a_permeabilidade_via_aerea'
  | 'b_frequencia_respiratoria'
  | 'b_ausculta_pulmonar'
  | 'b_expansao_toracica'
  | 'c_pulso_radial'
  | 'c_pulsos_especificos'
  | 'c_perfusao_periferica'
  | 'c_pressao_arterial'
  | 'c_pele_temperatura_umidade'
  | 'c_estase_jugular'
  | 'd_escala_glasgow'
  | 'd_nivel_avdi'
  | 'd_pupilas'
  | 'e_exposicao_dorso'
  | 'sample_sintomas'
  | 'sample_alergias'
  | 'sample_medicamentos'
  | 'sample_passado_medico'
  | 'sample_liquidos_alimentos'
  | 'sample_evento_trauma'
  | 'sec_cabeca_pescoco'
  | 'sec_torax_abdomen_pelve'
  | 'sec_membros_dorso'
  | 'sinais_spo2'
  | 'sinais_glicemia'
  | 'sinais_temperatura'
  | 'sinais_escala_dor'

export type EstadoDadoClinico = 'observado' | 'negativo' | 'incerto' | 'nao_informado'

/** As seis letras do exame primário XABCDE, usadas como chave curta em vários lugares da sessão. */
export type LetraXabcde = 'X' | 'A' | 'B' | 'C' | 'D' | 'E'

/** Fases do roteiro de aula conduzido pelo instrutor, na ordem em que aparecem no stepper. */
export type FaseAtendimento = 'abordagem' | 'intervencao' | 'extracao'

export type SituacaoSegurancaCena = 'nao_avaliada' | 'segura' | 'com_risco'
export type ItemAvaliacao360 = 'seguranca' | 'vitimas' | 'ejetadas' | 'mecanismo' | 'riscos'
export type GrauEncarceramento = 'nao_avaliado' | 'sem_encarceramento' | 'mecanico' | 'tf1' | 'tf2'
export type AcaoEquipeAtendimento =
  | 'aph1_verbalizou'
  | 'aph1_explicou_cena'
  | 'aph1_orientou_acalmou'
  | 'aph1_estabilizou_cervical'
  | 'aph1_administrou_oxigenio'
  | 'aph2_busca_acesso'
  | 'aph2_protegeu_ferragens'
  | 'equipe_trocou_luvas_aph'

export interface AvaliacaoCenaTabletop {
  readonly seguranca: SituacaoSegurancaCena
  readonly quantidadeVitimas: number | null
  readonly vitimasEjetadas: number | null
  readonly itensVerificados: readonly ItemAvaliacao360[]
}

export interface RegistroAcaoEquipe {
  readonly tipo: AcaoEquipeAtendimento
  readonly tempoSimuladoSegundos: number
}

export const FASES_ATENDIMENTO_ORDENADAS: readonly FaseAtendimento[] = [
  'abordagem', 'intervencao', 'extracao',
]

/** Compatibilidade com sessões anteriores à organização em três fases. */
export function normalizarFaseAtendimento(fase: unknown): FaseAtendimento {
  if (fase === 'intervencao' || fase === 'xabcde' || fase === 'secundaria') return 'intervencao'
  if (fase === 'extracao' || fase === 'decisao' || fase === 'debriefing') return 'extracao'
  return 'abordagem'
}

/** Guiado agrupa as ações por letra; Desafio embaralha a lista e cobra da equipe pedir o exame certo. */
export type NivelAtendimento = 'guiado' | 'desafio'

/** Leitura clínica que a própria equipe faz do achado revelado — não é o gabarito do instrutor. */
export type InterpretacaoLetra = 'sem_alteracao' | 'alterado' | 'ameaca_vida'

export type RegiaoAnatomicaVitima =
  | 'cabeca'
  | 'torax'
  | 'abdomen'
  | 'membroSuperiorDireito'
  | 'membroSuperiorEsquerdo'
  | 'membroInferiorDireito'
  | 'membroInferiorEsquerdo'
  | 'pelve'
  | 'dorso'
  | 'corpoTodo'

export interface DadoClinicoAcao {
  readonly valor: string
  /** Leitura curta para o monitor de sinais (ex.: "28 irpm"). Sem isso, usa-se o trecho antes da 1ª vírgula de `valor`. */
  readonly leitura?: string
  readonly detalhe?: string
  readonly estado: EstadoDadoClinico
  readonly notaInstrutor?: string
  readonly regiao?: RegiaoAnatomicaVitima
}

/**
 * Como a equipe obtém este dado — o professor encontra a resposta certa
 * sem o software "atender" no lugar da equipe:
 * - `pergunta`: histórico verbal (SAMPLE, dor relatada) — a equipe pergunta;
 * - `exame`: inspeção, palpação ou ausculta — a equipe examina;
 * - `afericao`: leitura numérica com aparelho ou escala padronizada
 *   (PA, FR, SpO₂, glicemia, temperatura, ECG, tempo de enchimento capilar).
 */
export type ModoAcaoAvaliacao = 'pergunta' | 'exame' | 'afericao'

export interface AcaoAvaliacaoDef {
  readonly id: IdAcaoAvaliacao
  readonly rotulo: string
  readonly etapa: EtapaAvaliacao
  readonly descricao: string
  readonly modo: ModoAcaoAvaliacao
  readonly regiaoPadrao?: RegiaoAnatomicaVitima
}

export type TipoRotaExtracao =
  | 'rota_rapida'
  | 'rota_lateral_porta'
  | 'rota_lateral_b'
  | 'rota_longitudinal_traseira'
  | 'rota_vertical_teto'
  | 'autoextracao_assistida'

export interface DefRotaExtracao {
  readonly id: TipoRotaExtracao
  readonly rotulo: string
  readonly descricao: string
}

/** Situação de um problema clínico da vítima ao longo da sessão. */
export type EstadoProblemaClinico = 'ativo' | 'parcial' | 'controlado'

/** O que uma regra prevê para o problema quando a intervenção é executada. */
export type ResultadoEfeito = 'sem_efeito' | 'parcial' | 'controlado'

/**
 * Intervenções que o instrutor registra com poucos toques. `string` continua
 * aceito em `RegistroIntervencaoAtendimento.tipo` para registros antigos.
 */
export type TipoIntervencaoClinica =
  | 'compressao_direta'
  | 'curativo_compressivo'
  | 'torniquete'
  | 'colar_cervical'
  | 'oxigenioterapia'
  | 'manta_termica'
  | 'imobilizacao_membro'

/**
 * Um problema preparado pelo autor da ficha (ex.: hemorragia na coxa
 * direita). Nasce ativo; só muda por uma regra de efeito ou por ajuste
 * manual do instrutor — nunca porque "uma intervenção qualquer" foi feita.
 */
export interface ProblemaClinicoFicha {
  readonly id: string
  readonly rotulo: string
  readonly regiao: RegiaoAnatomicaVitima
  /** aparece na cena 3D (ex.: sangramento) enquanto não estiver controlado */
  readonly visivelNaCena?: boolean
  /** encerrar o atendimento com ele sem controle vira pendência crítica no debriefing */
  readonly critico?: boolean
  /**
   * Achados que passam a valer quando o problema muda de estado. Só estes
   * mudam: controlar uma hemorragia não normaliza os demais sinais.
   */
  readonly achadosPorEstado?: Readonly<
    Partial<Record<'parcial' | 'controlado', Partial<Record<IdAcaoAvaliacao, DadoClinicoAcao>>>>
  >
}

/**
 * Regra determinística: "esta intervenção, aplicada nesta região, produz
 * este resultado neste problema". Sem regra para o par problema/intervenção
 * na região do problema, o efeito fica "a definir pelo instrutor".
 */
export interface RegraEfeitoIntervencao {
  readonly id: string
  readonly idProblema: string
  readonly tipoIntervencao: TipoIntervencaoClinica
  /** onde precisa ser aplicada para valer; ausente = qualquer região */
  readonly regioes?: readonly RegiaoAnatomicaVitima[]
  readonly resultado: ResultadoEfeito
  readonly explicacao: string
}

/**
 * Um ponto da evolução clínica além do inicial (ex.: compensação,
 * deterioração, crítico). Os achados aqui sobrescrevem os do estágio
 * anterior só nos itens listados — o resto continua valendo do estágio de
 * antes, em cascata a partir de `achadosIniciais`.
 */
export interface EstagioClinicoFicha {
  readonly id: string
  readonly rotulo: string
  readonly achados: Readonly<Partial<Record<IdAcaoAvaliacao, DadoClinicoAcao>>>
}

/**
 * O mecanismo de trauma como cena 3D: qual colisão, contra o quê, em que
 * assento e com que retenção. Alimenta o botão "Ver cinemática desta vítima"
 * da Oficina, que leva a aba Cinemática do app para este estado — não é uma
 * cena própria da Oficina (ver `OficinaVitimas.tsx`).
 */
export interface CinematicaFichaVitima {
  readonly tipoColisao: TipoColisao
  readonly alvoImpacto: TipoAlvoImpacto
  /** âncora do perfil do veículo em uso (ex.: 'motorista', 'passageiro', 'traseiroEsquerdo') */
  readonly idAncora: string
  readonly retencao: ModoRetencao
  /** km/h; ausente = a velocidade didática padrão daquele tipo de colisão */
  readonly velocidadeImpacto?: number
  /**
   * O que a ficha prevê como lesão provável deste mecanismo — escrito por
   * quem monta a ficha, nunca calculado a partir da física. Alimenta o
   * debriefing "lesões previsíveis × investigadas".
   */
  readonly regioesPreviveis?: readonly RegiaoAnatomicaVitima[]
}

export interface FichaVitimaAtendimento {
  readonly id: string
  readonly titulo: string
  readonly perfil: {
    readonly papel: string
    readonly mecanismo: string
    /** resumo do caso para o instrutor ("Descrição" do simulador de treinamento); sem ele, vale papel + mecanismo */
    readonly descricao?: string
    readonly condicaoDescrita: string
    readonly ator: string
    readonly aparencia: string
  }
  readonly achadosIniciais: Readonly<Record<IdAcaoAvaliacao, DadoClinicoAcao>>
  /** @deprecated preencher `estagios` com um estágio `tardio`; ainda lido pelas fichas antigas */
  readonly achadosTardios?: Readonly<Partial<Record<IdAcaoAvaliacao, DadoClinicoAcao>>>
  /**
   * Linha do tempo clínica desta ficha, na ordem em que a vítima evolui
   * (ex.: compensação → deterioração → crítico). "Inicial" sempre existe e
   * não entra nesta lista. Ausente = a ficha só tem `achadosTardios` (ou
   * nenhuma evolução).
   */
  readonly estagios?: readonly EstagioClinicoFicha[]
  /** ausente para vítimas sem mecanismo veicular (ex.: pedestre atropelado) */
  readonly cinematica?: CinematicaFichaVitima
  readonly intervencoesCitadas: readonly string[]
  readonly pendencias: readonly string[]
  /** problemas com estado e efeitos configurados; fichas antigas não têm */
  readonly problemas?: readonly ProblemaClinicoFicha[]
  readonly regrasEfeito?: readonly RegraEfeitoIntervencao[]
  /** de onde vêm as regras e em que versão — aparece no debriefing */
  readonly referenciaConteudo?: { readonly fonte: string; readonly versao: string }
  readonly gabaritoInstrutor?: {
    readonly triagemSugerida: PrioridadeTriagem
    readonly justificativaTriagem: string
    readonly prioridadeExtracao: 'imediata' | 'urgente' | 'estavel'
    readonly rotasRecomendadas: readonly TipoRotaExtracao[]
    readonly notasDebriefing: readonly string[]
  }
}

export interface PacoteOcorrenciaAtendimento {
  readonly versao: 1
  readonly nome: string
  readonly cena: CenaTabletop
  readonly protocoloDeclarado: {
    readonly nome: string
    readonly versao: string
    readonly populacao: string
    readonly fonte: string
  }
  readonly fichas: readonly FichaVitimaAtendimento[]
  readonly vinculos: ReadonlyArray<{
    readonly objetoVitimaId: string
    readonly fichaVitimaId: string
  }>
}

/** O que uma intervenção registrada fez (ou não) com um problema da vítima. */
export interface EfeitoIntervencao {
  readonly idProblema: string
  readonly rotuloProblema: string
  readonly estadoAntes: EstadoProblemaClinico
  readonly estadoDepois: EstadoProblemaClinico
  /** `a_definir`: não há regra para este par — o sistema não inventa melhora nem piora */
  readonly resultado: ResultadoEfeito | 'a_definir'
  readonly idRegra?: string
  readonly explicacao: string
}

export interface RegistroIntervencaoAtendimento {
  readonly id: string
  readonly tipo: string
  readonly rotulo: string
  readonly parametros: Readonly<Record<string, string | number | boolean>>
  readonly tempoSimuladoSegundos: number
  readonly justificativa?: string
  /** onde a equipe declarou ter aplicado */
  readonly regiao?: RegiaoAnatomicaVitima
  readonly efeitos?: readonly EfeitoIntervencao[]
}

/** Uma leitura registrada de um exame: guardada com horário, nunca sobrescrita. */
export interface LeituraRegistrada {
  readonly id: string
  readonly idAcao: IdAcaoAvaliacao
  readonly tempoSimuladoSegundos: number
  readonly achado: DadoClinicoAcao
  readonly tipo: 'avaliacao' | 'reavaliacao'
}

/** Por que o instrutor mudou o estado de um problema à mão. */
export type MotivoAjusteManual =
  | 'execucao_inadequada'
  | 'efeito_nao_previsto'
  | 'decisao_do_instrutor'
  | 'correcao_de_registro'

export interface AjusteManualProblema {
  readonly id: string
  readonly idProblema: string
  readonly estadoAntes: EstadoProblemaClinico
  readonly estadoDepois: EstadoProblemaClinico
  readonly motivo: MotivoAjusteManual
  readonly tempoSimuladoSegundos: number
}

/** O que a equipe fez num risco da cena — um toque do instrutor. */
export type TipoAcaoRisco =
  | 'varrer_vidro'
  | 'cobrir_vidro'
  | 'isolar_area'
  | 'conter_vazamento'
  | 'aplicar_absorvente'
  | 'acionar_concessionaria'
  | 'confirmar_desligamento'
  | 'combater_incendio'

export interface RegistroAcaoRisco {
  readonly id: string
  readonly tipo: TipoAcaoRisco
  /** só em `aplicar_absorvente` */
  readonly material?: MaterialAbsorvente
  readonly tempoSimuladoSegundos: number
}

/** Situação de um risco da cena (calculada pelas ações ou ajustada pelo instrutor). */
export type SituacaoRisco = 'ativo' | 'parcial' | 'controlado'

/**
 * O instrutor decidiu a situação do risco à mão (ex.: absorvente sem efeito
 * configurado, execução ruim). `situacaoDepois: null` devolve ao cálculo.
 */
export interface AjusteManualRisco {
  readonly id: string
  readonly situacaoAntes: SituacaoRisco
  readonly situacaoDepois: SituacaoRisco | null
  readonly motivo: MotivoAjusteManual
  readonly tempoSimuladoSegundos: number
}

/** Estado de um risco na sessão: calculado a partir das ações, que ficam todas guardadas. */
export interface EstadoRiscoNaSessao {
  readonly identificado: boolean
  readonly acoes: readonly RegistroAcaoRisco[]
  /** ausente em sessões salvas antes do ajuste manual existir */
  readonly ajustesManuais?: readonly AjusteManualRisco[]
}

/** O que a equipe fez num veículo durante a sessão (oficinas de corte, vidros e estabilização). */
export type TipoOperacaoVeiculo =
  | 'corte'
  | 'vidro'
  | 'protecao'
  | 'equipamento_colocado'
  | 'equipamento_retirado'
  | 'estabilizado'

export interface RegistroOperacao {
  readonly id: string
  readonly idVeiculo: string
  readonly tipo: TipoOperacaoVeiculo
  readonly descricao: string
  /** erros pedagógicos percebidos no momento (não bloqueiam a ação) — só o instrutor vê */
  readonly alertas: readonly string[]
  readonly tempoSimuladoSegundos: number
}

/** Marcos da ocorrência registrados com um toque, no tempo simulado. */
export type TipoMarcoOcorrencia = 'despacho' | 'deslocamento' | 'chegada' | 'atualizacao_cobom'

export interface MarcoOcorrencia {
  readonly id: string
  readonly tipo: TipoMarcoOcorrencia
  readonly tempoSimuladoSegundos: number
}

export interface EstadoVitimaNaSessao {
  /** 'inicial' sempre existe; senão, o id de um estágio de `FichaVitimaAtendimento.estagios` (ou 'tardio' nas fichas antigas) */
  readonly estadoEvolucao: string
  readonly triagemDeclarada?: PrioridadeTriagem
  readonly justificativaTriagem?: string
  /** Encarceramento estrutural; mantido separado da classificação clínica. */
  readonly grauEncarceramento?: GrauEncarceramento
  readonly acoesEquipe?: readonly RegistroAcaoEquipe[]
  readonly rotaDeclarada?: TipoRotaExtracao
  readonly justificativaRota?: string
  readonly acoesReveladas: readonly IdAcaoAvaliacao[]
  /** Interpretação que a própria equipe deu ao conjunto de achados de cada letra do XABCDE. */
  readonly interpretacoes: Readonly<Partial<Record<LetraXabcde, InterpretacaoLetra>>>
  readonly intervencoesRealizadas: readonly RegistroIntervencaoAtendimento[]
  /** todas as leituras registradas, em ordem; a mais recente de cada exame é a que vale */
  readonly leituras: readonly LeituraRegistrada[]
  /** exames cuja leitura mais recente o instrutor decidiu mostrar à turma */
  readonly liberadasNaTela: readonly IdAcaoAvaliacao[]
  /** estado atual de cada problema da ficha; ausente = ainda no estado inicial (ativo) */
  readonly problemas: Readonly<Record<string, EstadoProblemaClinico>>
  readonly ajustesManuais: readonly AjusteManualProblema[]
  readonly reavaliacoesRealizadas: ReadonlyArray<{
    readonly id: string
    readonly tempoSimuladoSegundos: number
    readonly observacao: string
  }>
}

export interface EventoHistoricoAtendimento {
  readonly id: string
  readonly tempoSimuladoSegundos: number
  readonly tipo:
    | 'revelacao'
    | 'triagem'
    | 'intervencao'
    | 'evolucao'
    | 'rota'
    | 'anotacao'
    | 'interpretacao'
    | 'fase'
    | 'identificacao'
    | 'reavaliacao'
    | 'ajuste'
    | 'marco'
    | 'risco'
    | 'operacao'
    | 'cena'
    | 'equipe'
    | 'encarceramento'
  readonly objetoVitimaId: string
  readonly descricao: string
  readonly detalhes?: string
}

export interface SessaoAtendimentoTabletop {
  readonly id: string
  readonly tempoSimuladoSegundos: number
  readonly cronometroRodando: boolean
  readonly modoExibicao: 'aluno' | 'instrutor'
  readonly fase: FaseAtendimento
  readonly nivel: NivelAtendimento
  readonly focoVitimaId: string | null
  readonly destaqueRegiaoAnatomica: RegiaoAnatomicaVitima | null
  readonly estadosVitimas: Readonly<Record<string, EstadoVitimaNaSessao>>
  /**
   * Vítimas que a guarnição já encontrou (marcadas pelo instrutor durante a
   * volta 360°). Só estas ganham cartão, rótulo e contagem na projeção; as
   * demais continuam na cena 3D como a equipe as veria, mas sem destaque.
   */
  readonly vitimasIdentificadas: readonly string[]
  /** Avaliação de segurança e varredura 360° feita pela guarnição. */
  readonly avaliacaoCena?: AvaliacaoCenaTabletop
  readonly luvasAPHConfirmadas?: boolean
  readonly marcos: readonly MarcoOcorrencia[]
  /** estado dos riscos da cena, por id do objeto; ausente = ainda não identificado, nada feito */
  readonly riscos: Readonly<Record<string, EstadoRiscoNaSessao>>
  /** cortes, vidros, proteções e estabilização feitos nos veículos durante a sessão */
  readonly operacoes: readonly RegistroOperacao[]
  readonly historicoEventos: readonly EventoHistoricoAtendimento[]
}
